import React, { useState } from "react";
import { toast } from "sonner";
import useCrmApi from "../../../components/hook/crm-req";
import { DealStage, stageLabel } from "./types";

const inputClass =
  "w-full px-3 py-2.5 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-950 text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-200 dark:focus:ring-indigo-500/20";

// Shared PATCH /deals/{id}/stage flow — extracted out of stage-menu.tsx's
// dropdown so the kanban board's drag-and-drop can trigger the exact same
// real request (and the same "won"/"lost" extra-info prompts) as picking a
// stage from the dropdown does. One instance of this hook, wherever it's
// used, drives whichever deal is currently being acted on (stage-menu.tsx
// keeps one per card since it already knows its own dealId; the board in
// index.tsx keeps a single shared instance since a drop can land on any card).
export function useStageChange(onChanged: (dealId: string, stage: DealStage) => void) {
  const token = localStorage.getItem("authToken");
  const { makeRequest: updateStage } = useCrmApi("/deals", "PATCH", { Authorization: `Bearer ${token}` });

  const [submitting, setSubmitting] = useState(false);
  const [target, setTarget] = useState<{ dealId: string; currentStage: DealStage } | null>(null);
  const [wonModalOpen, setWonModalOpen] = useState(false);
  const [lostModalOpen, setLostModalOpen] = useState(false);
  const [txId, setTxId] = useState("");
  const [orderId, setOrderId] = useState("");
  const [lossReason, setLossReason] = useState("");

  const submitStage = async (dealId: string, nextStage: DealStage, extra?: Record<string, unknown>) => {
    setSubmitting(true);
    const payload: Record<string, unknown> = { stage: nextStage, ...extra };
    const [response, status] = await updateStage(payload, undefined, `${dealId}/stage`);
    setSubmitting(false);
    if (status >= 200 && status < 300) {
      toast.success(`Deal marked ${stageLabel(nextStage)}.`);
      onChanged(dealId, nextStage);
    } else {
      toast.error(response?.detail || response?.message || "Failed to update stage.");
    }
  };

  // dealId/currentStage identify which deal this particular request is for
  // — passed at call time rather than baked into the hook, so one shared
  // instance (the board) can act on whichever card was just dropped.
  const requestStageChange = (dealId: string, currentStage: DealStage, nextStage: DealStage) => {
    if (nextStage === currentStage) return;
    setTarget({ dealId, currentStage });
    if (nextStage === "won") {
      setWonModalOpen(true);
      return;
    }
    if (nextStage === "lost") {
      setLostModalOpen(true);
      return;
    }
    submitStage(dealId, nextStage);
  };

  const modals = (
    <>
      {wonModalOpen && target && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" onClick={(e) => e.stopPropagation()}>
          <div className="absolute inset-0 bg-black/50" onClick={() => setWonModalOpen(false)} />
          <div className="relative w-full max-w-sm bg-white dark:bg-gray-900 rounded-xl shadow-2xl p-5">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-1">Mark deal as won</h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
              Optional — link an external transaction/order. Leave blank and an internal completed transaction is created automatically.
            </p>
            <div className="space-y-2.5">
              <input value={txId} onChange={(e) => setTxId(e.target.value)} placeholder="External transaction ID (optional)" className={inputClass} />
              <input value={orderId} onChange={(e) => setOrderId(e.target.value)} placeholder="Order ID (optional)" className={inputClass} />
            </div>
            <div className="flex items-center justify-end gap-2.5 mt-4">
              <button
                type="button"
                onClick={() => setWonModalOpen(false)}
                className="px-3.5 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={submitting}
                onClick={async () => {
                  const transaction =
                    txId.trim() || orderId.trim()
                      ? {
                          ...(txId.trim() ? { external_transaction_id: txId.trim() } : {}),
                          ...(orderId.trim() ? { order_id: orderId.trim() } : {}),
                          status: "completed",
                        }
                      : undefined;
                  await submitStage(target.dealId, "won", transaction ? { transaction } : undefined);
                  setWonModalOpen(false);
                  setTxId("");
                  setOrderId("");
                }}
                className="px-3.5 py-2 text-sm font-semibold text-white bg-green-600 hover:bg-green-700 disabled:opacity-60 rounded-lg"
              >
                {submitting ? "Saving…" : "Mark Won"}
              </button>
            </div>
          </div>
        </div>
      )}

      {lostModalOpen && target && (
        <div className="fixed inset-0 z-[70] flex items-center justify-center p-4" onClick={(e) => e.stopPropagation()}>
          <div className="absolute inset-0 bg-black/50" onClick={() => setLostModalOpen(false)} />
          <div className="relative w-full max-w-sm bg-white dark:bg-gray-900 rounded-xl shadow-2xl p-5">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-1">Mark deal as lost</h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">Optional — note why this deal was lost.</p>
            <input value={lossReason} onChange={(e) => setLossReason(e.target.value)} placeholder="Loss reason (optional)" className={inputClass} />
            <div className="flex items-center justify-end gap-2.5 mt-4">
              <button
                type="button"
                onClick={() => setLostModalOpen(false)}
                className="px-3.5 py-2 text-sm font-medium text-gray-600 dark:text-gray-300 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={submitting}
                onClick={async () => {
                  await submitStage(target.dealId, "lost", lossReason.trim() ? { loss_reason: lossReason.trim() } : undefined);
                  setLostModalOpen(false);
                  setLossReason("");
                }}
                className="px-3.5 py-2 text-sm font-semibold text-white bg-rose-600 hover:bg-rose-700 disabled:opacity-60 rounded-lg"
              >
                {submitting ? "Saving…" : "Mark Lost"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );

  return { requestStageChange, modals, submitting };
}
